import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
	Box,
	Typography,
	IconButton,
	Divider,
	TextField,
	Tooltip,
	Link,
	MenuItem
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import SingleSelect from 'components/common/SingleSelect';
import { buildCrmRecordUrl } from 'features/IRWorkstation/utils/overview';
import { validateSalesforceTaskDueDate, isSalesforceTaskCommentsTooLong } from './salesforceTaskValidation';
import SalesforceTaskDueDatePicker from './SalesforceTaskDueDatePicker';
import salesforceTasksService from 'services/salesforceTasksService';
import Drawer from '../Common/Drawer/Drawer';
import SalesforceTaskDrawerSkeleton from './SalesforceTaskDrawerSkeleton';
import SalesforceTaskCompletedBanner from './SalesforceTaskCompletedBanner';
import SalesforceTaskCommentsField from './SalesforceTaskCommentsField';
import useTaskFieldUpdate from '../../hooks/useTaskFieldUpdate';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

const CONVERT_TO_INTERACTION_OPTIONS = ['No', 'Yes'];
const CONVERT_TO_INTERACTION_HELP_TEXT = 'On completion of this Task, should it be converted into an Interaction record to be logged as activity with the related record(s)?';

const fieldStyles = {
	'& .MuiInputBase-root': {
		fontSize: '12px',
		borderRadius: '6px',
		backgroundColor: 'background.paper',
		minHeight: '18px',
	},
	'& .MuiInputBase-input': {
		fontSize: '12px',
	},
	'& .MuiFormHelperText-root': {
		fontSize: '10px',
		marginLeft: 0,
		// Minimize size to prevent layout shift:
		height: '4px',
		margin: 0,
		padding: '1px',
	},
};

const TaskField = ({ label, action, children, helperText }) => (
	<Box
		sx={{
			display: 'grid',
			gridTemplateColumns: action
				? '160px minmax(0, 1fr) auto'
				: '160px minmax(0, 1fr)',
			alignItems: 'flex-start',
			gap: '6px',
			py: 2,
		}}
	>
		<Typography
			component='label'
			sx={{
				fontSize: '11px',
				fontWeight: 500,
				color: 'text.secondary',
				alignSelf: 'center',
				display: 'flex',
				alignItems: 'center',
				gap: '4px',
			}}
		>
			{label}
			{helperText && (
				<Tooltip title={helperText} placement='top' arrow>
					<InfoOutlinedIcon
						sx={{
							fontSize: 13,
							color: 'text.secondary',
							cursor: 'help',
							flexShrink: 0,
						}}
					/>
				</Tooltip>
			)}
		</Typography>

		<Box sx={{ minWidth: 0 }}>{children}</Box>

		{action && (
			<Box
				sx={{
					minWidth: 0,
					my: 0.5,
				}}
			>
				{action}
			</Box>
		)}
	</Box>
);

TaskField.propTypes = {
	label: PropTypes.string.isRequired,
	action: PropTypes.node,
	children: PropTypes.node,
  helperText: PropTypes.string,
};

const SalesforceTaskDrawer = ({
	open,
	task,
	tenant,
	onClose,
	onTaskUpdated,
	salesforceOrgUrl,
}) => {
	const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });
	const commentsInputRef = useRef(null);

	// Metadata needed to render Priority and Assigned To options
	const [formOptions, setFormOptions] = useState(null);
	const [formOptionsLoading, setFormOptionsLoading] = useState(false);
	const [assignableUsers, setAssignableUsers] = useState([]);
	const [priorities, setPriorities] = useState([]);

	// Currently selected due date in the DatePicker
	const [dueDate, setDueDate] = useState(null);
	// Last saved due date used to detect unsaved changes
	const [savedDueDate, setSavedDueDate] = useState(null);
	// Tracks whether the user has edited the due date
	const [hasChangedDueDate, setHasChangedDueDate] = useState(false);

	// Subject state
	const [subject, setSubject] = useState('');
	const [savedSubject, setSavedSubject] = useState('');

	// Assigned To state
	const [assignedTo, setAssignedTo] = useState('');
	const [savedAssignedTo, setSavedAssignedTo] = useState('');

	// Comments state
	const [isEditingComments, setIsEditingComments] = useState(false);
	const [comments, setComments] = useState('');
	const [savedComments, setSavedComments] = useState('');

	const trimComments = (value) => value?.trim() || '';

	const showCompletedBanner =
		task?.status === 'Completed' &&
		task?.completed_at &&
		dayjs(task.completed_at).isSame(dayjs(), 'day');

	// Load the same form options used in the create task form so we can
	// display the Priority and Assigned To choices and names.
	useEffect(() => {
		if (!open || formOptions) {
			return;
		}

		let isCurrent = true;

		const loadFormOptions = async () => {
			setFormOptionsLoading(true);

			try {
				const options = await salesforceTasksService.getFormOptions();

				if (!isCurrent) {
					return;
				}

				setFormOptions(options);
				setAssignableUsers(options?.assignable_users ?? []);
				setPriorities(options?.priorities ?? []);
			} catch (error) {
				console.error('Failed to load Salesforce task form options:', error);
			} finally {
				if (isCurrent) {
					setFormOptionsLoading(false);
				}
			}
		};

		loadFormOptions();

		return () => {
			isCurrent = false;
		};
	}, [open, formOptions]);

	// Tracks which task.id we last initialized local state from, so we only
	// reset the whole drawer when the user is looking at a *different* task or closes the drawer —
	// not every time this task's data is refreshed after a single-field save.
	const initializedTaskIdRef = useRef(null);

	const initializeDrawerState = useCallback((currentTask) => {
		const taskDueDate = currentTask?.due_date ?? null;
		const taskComments = trimComments(currentTask?.comments);
		const taskSubject = currentTask?.subject ?? '';
		const taskOwnerId =
			currentTask?.owner_id ??
			assignableUsers.find((user) => user.is_current_user)
				?.sf_user_id_in_client_org ??
			'';

		setDueDate(taskDueDate ? dayjs(taskDueDate) : null);
		setSavedDueDate(taskDueDate);
		setHasChangedDueDate(false);

		setSubject(taskSubject);
		setSavedSubject(taskSubject);

		setAssignedTo(taskOwnerId);
		setSavedAssignedTo(taskOwnerId);

		setComments(taskComments);
		setSavedComments(taskComments);
		setIsEditingComments(false);
	}, [assignableUsers]);

	useEffect(() => {
		if (!open) return;

		const isNewTask = initializedTaskIdRef.current !== task.id;

		if (isNewTask) {
			initializedTaskIdRef.current = task.id;
			initializeDrawerState(task);
		}

		// If it's the same task.id, this is just a refreshed `task` object
		// (e.g. after editing a single field) - let other unsaved edits persist.
	}, [open, task, assignableUsers, initializeDrawerState]);

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.TaskDrawer);
	}, []);

	// Place the cursor at the end of the existing comments when entering edit mode
	useEffect(() => {
		if (!isEditingComments) return;

		const textarea = commentsInputRef.current;
		if (!textarea) return;

		const length = textarea.value.length;
		textarea.focus();
		textarea.setSelectionRange(length, length);
	}, [isEditingComments]);

	// -------------------------------------------------------------------------
	// Button Click Track Event
	// -------------------------------------------------------------------------
	const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			status: ButtonClickTargets.TaskDrawerStatus,
			changeDueDate: ButtonClickTargets.TaskDrawerChangeDueDate,
			comments: ButtonClickTargets.TaskDrawerComments,
			viewRecordInCrm: ButtonClickTargets.TaskDrawerViewRecordInCrm,
			close: ButtonClickTargets.TaskDrawerClose,
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

	// -------------------------------------------------------------------------
	// Subject
	// -------------------------------------------------------------------------
	const isSubjectUnchanged = subject.trim() === savedSubject.trim();
	const isSubjectInvalid = !subject.trim();

	const { save: saveSubject, loading: subjectLoading } = useTaskFieldUpdate({
		task,
		field: 'subject',
		successMessage: 'Task subject updated successfully',
		errorMessage: 'Unable to update task subject',
		onTaskUpdated,
	});

	const handleSubjectChange = (event) => {
		setSubject(event.target.value);
	};

	const handleUpdateSubject = async () => {
		if (!task || isSubjectInvalid || isSubjectUnchanged || subjectLoading) {
			return;
		}

		try {
			const updatedTask = await saveSubject(subject.trim());
			const updatedSubject = updatedTask?.subject ?? subject.trim();

			setSavedSubject(updatedSubject);
			setSubject(updatedSubject);
		} catch (err) {
			console.error(err);
			// Notification already shown by the hook.
		}
	};

	// -------------------------------------------------------------------------
	// Assigned To
	// -------------------------------------------------------------------------
	const { save: saveAssignedTo, loading: assignedToLoading } = useTaskFieldUpdate({
		task,
		field: 'owner_id',
		successMessage: 'Task assignment updated successfully',
		errorMessage: 'Unable to update task assignment',
		onTaskUpdated,
	});

	const handleUpdateAssignedTo = async (ownerId) => {
		if (!task || ownerId === savedAssignedTo || assignedToLoading) {
			return;
		}

		const userName = assignableUsers.find(
			(user) => user.sf_user_id_in_client_org === ownerId
		)?.name;

		const confirmed = window.confirm(
			`Reassign this task${userName ? ` to ${userName}` : ''}? You won't see it in your work queue anymore since tasks are only visible to their assigned owner.`
		);

		if (!confirmed) {
			return;
		}

		try {
			// Show loading state so entire form is disabled -
			// user can only update their own assigned tasks
			// once owner_id is updated they'd get errors
			// attempting to edit other fields
			setFormOptionsLoading(true);

			const updatedTask = await saveAssignedTo(ownerId);
			const updatedOwnerId = updatedTask?.owner_id ?? ownerId;

			setSavedAssignedTo(updatedOwnerId);

			handleCloseDrawer();
		} catch (err) {
			setFormOptionsLoading(false);
			setAssignedTo(savedAssignedTo);
			console.error(err);
			// Notification already shown by the hook.
		}
	};

	// -------------------------------------------------------------------------
	// Due date
	// -------------------------------------------------------------------------
	const handleDueDateChange = (value) => {
		setHasChangedDueDate(true);
		setDueDate(value);
	};

	// Allow viewing an existing past due date, but prevent changing to a new past date
	const dueDateValidation = validateSalesforceTaskDueDate(
		dueDate,
		task?.due_date
	);

	const isDueDateInvalid = !dueDateValidation.isValid;

	// Prevent unnecessary updates when the due date has not changed.
	const isUnchangedDueDate = dueDateValidation.formattedValue === savedDueDate;

	const { save: saveDueDate, loading: dueDateLoading } = useTaskFieldUpdate({
		task,
		field: 'due_date',
		successMessage: (updatedTask) => `Task due date changed to ${updatedTask.due_date}`,
		errorMessage: 'Unable to update task due date',
		onTaskUpdated,
	});

	// Persist the updated due date to Salesforce and refresh task data on success
	const handleUpdateDueDate = async () => {
		if (!task || isDueDateInvalid || isUnchangedDueDate) return;
		handleButtonClickTrackEvent('changeDueDate');

		try {
			const updatedTask = await saveDueDate(dueDateValidation.formattedValue);

			// Sync local state with the saved value so the button disables immediately
			setSavedDueDate(updatedTask.due_date);
			setDueDate(dayjs(updatedTask.due_date));
			setHasChangedDueDate(false);
		} catch {
			// Revert to the last saved value
			setDueDate(savedDueDate ? dayjs(savedDueDate) : null);
		}
	};

	// -------------------------------------------------------------------------
	// Status
	// -------------------------------------------------------------------------
	const { save: saveStatus, loading: statusLoading } = useTaskFieldUpdate({
		task,
		field: 'status',
		successMessage: (updatedTask) => `Task status changed to ${updatedTask.status}`,
		errorMessage: 'Unable to update task status',
		onTaskUpdated,
	});

	const handleUpdateStatus = async (status) => {
		if (!task || status === task.status) return;
		handleButtonClickTrackEvent('status');

		// Re-throws on failure so SingleSelect can revert its selection.
		await saveStatus(status);
	};

	// -------------------------------------------------------------------------
	// Priority
	// -------------------------------------------------------------------------
	const { save: savePriority, loading: priorityLoading } = useTaskFieldUpdate({
		task,
		field: 'priority',
		successMessage: (updatedTask) => `Task priority changed to ${updatedTask.priority}`,
		errorMessage: 'Unable to update task priority',
		onTaskUpdated,
	});

	const handleUpdatePriority = async (priority) => {
		if (!task || priority === task.priority) return;

		// Re-throws on failure so SingleSelect can revert its selection.
		await savePriority(priority);
	};

	// -------------------------------------------------------------------------
	// Comments
	// -------------------------------------------------------------------------
	const isCommentsTooLong = isSalesforceTaskCommentsTooLong(comments);
	const isCommentsUnchanged =
		trimComments(comments) === trimComments(savedComments);

	const { save: saveComments, loading: commentsLoading } = useTaskFieldUpdate({
		task,
		field: 'comments',
		successMessage: 'Task comments updated successfully',
		errorMessage: 'Unable to update task comments',
		onTaskUpdated,
	});

	const handleStartEditingComments = () => {
		if (commentsLoading) return;

		setIsEditingComments(true);
	};

	const handleCommentsChange = (event) => {
		setComments(event.target.value);
	};

	const handleCancelCommentsEdit = () => {
		if (commentsLoading) return;

		setComments(savedComments);
		setIsEditingComments(false);
	};

	const handleUpdateComments = async () => {
		if (!task || isCommentsTooLong || isCommentsUnchanged) return;
		handleButtonClickTrackEvent('comments');

		try {
			const commentsToSave = trimComments(comments);
			const updatedTask = await saveComments(commentsToSave);
			const updatedComments = trimComments(updatedTask.comments);

			setComments(updatedComments);
			setSavedComments(updatedComments);
			setIsEditingComments(false);
		} catch (err) {
			console.error(err);
			// Notification already shown by the hook.
		}
	};

	// -------------------------------------------------------------------------
	// Convert to Interaction
	// -------------------------------------------------------------------------
	const { save: saveConvertToInteraction, loading: convertToInteractionLoading } = useTaskFieldUpdate({
		task,
		field: 'convert_to_interaction',
		successMessage: (updatedTask) => `Convert to Interaction changed to ${updatedTask.convert_to_interaction}`,
		errorMessage: 'Unable to update Convert to Interaction',
		onTaskUpdated,
	});

	const handleUpdateConvertToInteraction = async (convertToInteraction) => {
		if (!task || convertToInteraction === task.convert_to_interaction) return;

		// Re-throws on failure so SingleSelect can revert its selection.
		await saveConvertToInteraction(convertToInteraction);
	};


	// -------------------------------------------------------------------------
	// CRM links
	// -------------------------------------------------------------------------
	const renderCrmLink = (text, recordId) => {
		const url = buildCrmRecordUrl(salesforceOrgUrl, recordId);

		if (!url) {
			return (
				<Typography
					sx={{
						fontSize: '13px',
						fontWeight: 500,
						color: 'text.primary',
					}}
				>
					{text || '—'}
				</Typography>
			);
		}

		return (
			<Link
				href={url}
				target='_blank'
				rel='noopener noreferrer'
				sx={{
					fontSize: '13px',
					fontWeight: 500,
					color: 'primary.main',
					textDecoration: 'none',
					'&:hover': { textDecoration: 'underline' },
				}}
			>
				{text || '—'}
			</Link>
		);
	};

	// -------------------------------------------------------------------------
	// Drawer
	// -------------------------------------------------------------------------
	const handleCloseDrawer = () => {
		handleButtonClickTrackEvent('close');
		initializeDrawerState(task);
		initializedTaskIdRef.current = null; // force resync next time this drawer opens
		onClose();
	};

	const actionButtons = [
		task?.action_link && {
			key: 'task-action-link-button',
			label: 'View Task in CRM',
			href: task.action_link,
			variant: 'primary',
			onClick: () => {
				handleButtonClickTrackEvent('viewRecordInCrm');
			},
		},
	].filter(Boolean);

	const isLoading = formOptionsLoading || !formOptions;

	return (
		<Drawer
			open={open}
			onClose={handleCloseDrawer}
			title={task?.subject || 'Task Details'}
			actionButtons={actionButtons}
		>
				{/* Completed Today banner */}
				{showCompletedBanner && !isLoading && (
					<SalesforceTaskCompletedBanner task={task} />
				)}

				{isLoading ? (
					<SalesforceTaskDrawerSkeleton showCompletedBanner={showCompletedBanner}/>
				) : (
				<Box sx={{ flex: 1, px: 3, pt: 3 }}>
					<Box
						sx={{
							backgroundColor: 'background.subtle',
							borderRadius: '8px',
							padding: '8px 10px',
							mb: '16px',
						}}
					>
						<TaskField
							label='Subject'
							action={
								<LoadingButton
									data-testid='save-task-subject-button'
									variant='contained'
									size='small'
									loading={subjectLoading}
									disabled={
										subjectLoading ||
										isSubjectUnchanged ||
										isSubjectInvalid
									}
									onClick={handleUpdateSubject}
									sx={{ minWidth: 80 }}
								>
									Save
								</LoadingButton>
							}
						>
							<TextField
								fullWidth
								size='small'
								value={subject}
								disabled={subjectLoading}
								onChange={handleSubjectChange}
								error={isSubjectInvalid}
								helperText={
									isSubjectInvalid
										? 'Subject is required'
										: ' '
								}
								inputProps={{
									'data-testid': 'task-subject-input',
								}}
								sx={fieldStyles}
							/>
						</TaskField>

						<TaskField label='Assigned To'>
							<TextField
								select
								fullWidth
								size='small'
								value={assignedTo}
								onChange={(event) =>
									handleUpdateAssignedTo(event.target.value)
								}
								disabled={assignedToLoading || formOptionsLoading}
								SelectProps={{
									'data-testid': 'task-assigned-to-select',
								}}
								sx={fieldStyles}
							>
								{assignableUsers.map((user) => (
									<MenuItem
										key={user.sf_user_id_in_client_org}
										value={user.sf_user_id_in_client_org}
									>
										{user.name}
									</MenuItem>
								))}
							</TextField>
						</TaskField>

						<TaskField label='Status'>
							<SingleSelect
								value={task.status}
								options={task.statuses ?? []}
								loading={statusLoading}
								placeholder='Select a status'
								onChange={handleUpdateStatus}
							/>
						</TaskField>

						<TaskField label='Priority'>
							<SingleSelect
								value={task.priority}
								options={priorities}
								loading={priorityLoading || formOptionsLoading}
								placeholder='Select a priority'
								onChange={handleUpdatePriority}
							/>
						</TaskField>

						<TaskField
							label='Due Date'
							action={
								<LoadingButton
									data-testid='change-date-button'
									variant='contained'
									size='small'
									loadingPosition='start'
									loading={dueDateLoading}
									disabled={
										dueDateLoading ||
										isUnchangedDueDate ||
										isDueDateInvalid
									}
									onClick={handleUpdateDueDate}
									sx={{ minWidth: 100 }}
								>
									Change Date
								</LoadingButton>
							}
						>
							<SalesforceTaskDueDatePicker
								value={dueDate}
								onChange={handleDueDateChange}
								validation={dueDateValidation}
								disabled={dueDateLoading}
								showError={hasChangedDueDate}
							/>
						</TaskField>

            <TaskField
							label='Convert to Interaction?'
							helperText={CONVERT_TO_INTERACTION_HELP_TEXT}
						>
							<SingleSelect
								value={task.convert_to_interaction ?? 'No'}
								options={CONVERT_TO_INTERACTION_OPTIONS}
								loading={convertToInteractionLoading}
								placeholder='Select an option'
								onChange={handleUpdateConvertToInteraction}
								dataTestId='convert-to-interaction-select'
							/>
						</TaskField>

						<TaskField label='Related To'>
							{renderCrmLink(
								task.related_to,
								task.related_to_id
							)}
						</TaskField>

						<TaskField label='Name'>
							{renderCrmLink(task.name, task.name_id)}
						</TaskField>

						<TaskField
							label='Comments'
							action={
								!isEditingComments && (
									<Tooltip title='Edit comments'>
										<IconButton
											size='small'
											aria-label='Task comments'
											onClick={handleStartEditingComments}
											sx={{
												color: 'text.secondary',
												'&:hover': {
													color: 'primary.main',
													backgroundColor:
														'background.subtle',
												},
											}}
										>
											<EditOutlinedIcon fontSize='small' />
										</IconButton>
									</Tooltip>
								)
							}
						>
							{isEditingComments ? (
								<Box>
									<SalesforceTaskCommentsField
										value={comments}
										onChange={handleCommentsChange}
										disabled={commentsLoading}
										inputRef={commentsInputRef}
									/>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'flex-end',
											gap: 1,
											mt: 1.5,
										}}
									>
										<LoadingButton
											variant='text'
											size='small'
											disabled={commentsLoading}
											onClick={handleCancelCommentsEdit}
										>
											Cancel
										</LoadingButton>

										<LoadingButton
											data-testid='save-task-comments-button'
											variant='contained'
											size='small'
											loading={commentsLoading}
											disabled={
												commentsLoading ||
												isCommentsTooLong ||
												isCommentsUnchanged
											}
											onClick={handleUpdateComments}
										>
											Save
										</LoadingButton>
									</Box>
								</Box>
							) : (
								/**
								 * View Comments mode - clickable to edit
								 **/
								<Box
									role='button'
									tabIndex={0}
									aria-label='Edit comments'
									onClick={handleStartEditingComments}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											handleStartEditingComments();
										}
									}}
									sx={{
										position: 'relative',
										backgroundColor: 'background.subtle',
										borderRadius: '8px',
										padding: '8px',
										border: '1px solid',
										borderColor: 'border.light',
										cursor: 'pointer',
										minHeight: 40,
										transition:
											'border-color 150ms ease, background-color 150ms ease',
										'&:hover': {
											borderColor: 'primary.main',
											backgroundColor: 'action.hover',
										},
										'&:focus-visible': {
											outline: '2px solid',
											outlineColor: 'primary.main',
											outlineOffset: '2px',
										},
									}}
								>
									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 500,
											color: 'text.primary',
											whiteSpace: 'pre-wrap',
											overflowWrap: 'anywhere',
											maxHeight: 80,
											overflowY: 'auto',
											pr: 1,
										}}
									>
										{comments}
									</Typography>
								</Box>
							)}
						</TaskField>
					</Box>

					<Divider sx={{ my: 2 }} />

					<TaskField label='Created By'>
						<Typography
							sx={{
								fontSize: '13px',
								fontWeight: 500,
								color: 'text.primary',
								lineHeight: '14px',
							}}
						>
							{task.created_by || '—'}
						</Typography>
					</TaskField>

					<TaskField label='Last Modified By'>
						<Typography
							sx={{
								fontSize: '13px',
								fontWeight: 500,
								color: 'text.primary',
								lineHeight: '14px',
							}}
						>
							{task.last_modified_by || '—'}
						</Typography>
					</TaskField>
				</Box>
			)}
		</Drawer>
	);
};

SalesforceTaskDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	task: PropTypes.object,
	tenant: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
	onTaskUpdated: PropTypes.func.isRequired,
	salesforceOrgUrl: PropTypes.string,
};

export default SalesforceTaskDrawer;